import React, { useState, useEffect } from 'react';
import { RiMailLine, RiTimeLine, RiQuestionLine, RiSendPlaneLine, RiLoader4Line, RiFacebookFill, RiTwitterXFill, RiLinkedinFill, RiDiscordFill, RiCheckLine, RiErrorWarningLine } from 'react-icons/ri';

interface ACFFields {
  contact_us_title: string;
  contact_page_description_top: string;
  get_in_touch_title: string;
  get_in_touch_description: string;
  email_field: string;
  email_description: string;
  response_time: string;
  response_time_description: string;
  faq: string;
  faq_description: string;
}

interface GlobalSettings {
  twitter_url?: string;
  linkedin_url?: string;
  discord_url?: string;
  facebook_url?: string;
}

const ContactPage: React.FC = () => {
  const [acf, setAcf] = useState<ACFFields | null>(null);
  const [globals, setGlobals] = useState<GlobalSettings>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Fetch global settings
  useEffect(() => {
    fetch('https://blog.thecryptotutor.com/wp-json/custom/v1/globals')
      .then(res => res.json())
      .then(data => setGlobals(data))
      .catch(err => console.error('Failed to fetch globals:', err));
  }, []);

  // Fetch ACF fields
  useEffect(() => {
    const fetchACF = async () => {
      try {
        const res = await fetch('https://blog.thecryptotutor.com/wp-json/wp/v2/pages?slug=contact');
        const data = await res.json();
        if (data?.length > 0) {
          setAcf(data[0].acf);
        }
      } catch (err) {
        console.error('Error fetching Contact page ACF:', err);
      }
    };
    fetchACF();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://thecryptotutor.com/wp-json/custom/v1/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!acf) return <div className="text-center py-20">Loading contact page...</div>;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header Section */}
      <div className="bg-[#F5F7FA] py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{acf.contact_us_title}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{acf.contact_page_description_top}</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{acf.get_in_touch_title}</h2>
                <p className="text-gray-600 mb-8">{acf.get_in_touch_description}</p>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <RiMailLine className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Email Us</h3>
                  <p className="text-gray-600">{acf.email_field}</p>
                  <p className="text-sm text-gray-500">{acf.email_description}</p>
                </div>
              </div>

              {/* Response Time */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <RiTimeLine className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Response Time</h3>
                  <p className="text-gray-600">{acf.response_time}</p>
                  <p className="text-sm text-gray-500">{acf.response_time_description}</p>
                </div>
              </div>

              {/* FAQ */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <RiQuestionLine className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{acf.faq}</h3>
                  <p className="text-gray-600">{acf.faq_description}</p>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Follow Us</h3>
                <div className="flex space-x-3">
                  {globals.facebook_url && (
                    <a href={globals.facebook_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600">
                      <RiFacebookFill className="text-lg" />
                    </a>
                  )}
                  {globals.twitter_url && (
                    <a href={globals.twitter_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600">
                      <RiTwitterXFill className="text-lg" />
                    </a>
                  )}
                  {globals.linkedin_url && (
                    <a href={globals.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600">
                      <RiLinkedinFill className="text-lg" />
                    </a>
                  )}
                  {globals.discord_url && (
                    <a href={globals.discord_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600">
                      <RiDiscordFill className="text-lg" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>

              <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" placeholder="Enter your full name" />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" placeholder="Enter your email address" />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                  <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" placeholder="What's this about?" />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required maxLength={500} rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none" placeholder="Tell us how we can help you..." />
                  <div className="text-right text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</div>
                </div>

                <button type="submit" disabled={isSubmitting || formData.message.length > 500} className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
                  {isSubmitting ? <> <RiLoader4Line className="animate-spin mr-2 inline" /> Sending... </> : <>Send Message <RiSendPlaneLine className="ml-2 inline" /></>}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-2">
                    <div className="flex items-center">
                      <RiCheckLine className="text-green-600 mr-2" />
                      <span className="text-green-800 text-sm">Thank you! Your message has been sent successfully. We'll get back to you soon.</span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-2">
                    <div className="flex items-center">
                      <RiErrorWarningLine className="text-red-600 mr-2" />
                      <span className="text-red-800 text-sm">Sorry, there was an error sending your message. Please try again or contact us directly.</span>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
